import { EncodeOptions, RasterImage } from "./types";

/**
 * Codifica una imagen monocromática como comandos ESC/POS raster.
 *
 * Secuencia (research.md D3, contracts/bridge-local-api.md):
 *   ESC @                       -> init
 *   [por banda <=255 filas] GS v 0 m xL xH yL yH <bits>
 *   LF * feedLines              -> avance
 *   GS V 66 0                   -> corte con avance
 *
 * Función PURA: no toca DOM ni red. Testeable con Vitest.
 */
const MAX_BAND_ROWS = 255;

export function encodeEscposRaster(
  img: RasterImage,
  opts: EncodeOptions = {}
): Uint8Array {
  const { bytesPerRow, heightPx, data } = img;
  const feedLines = opts.feedLines ?? 3;
  const cut = opts.cut ?? true;

  if (bytesPerRow <= 0 || heightPx <= 0) {
    throw new Error("encodeEscposRaster: imagen vacía");
  }
  if (data.length !== bytesPerRow * heightPx) {
    throw new Error(
      `encodeEscposRaster: data.length (${data.length}) != bytesPerRow*heightPx (${bytesPerRow * heightPx})`
    );
  }

  const chunks: number[] = [];

  // ESC @  -> inicializar impresora
  chunks.push(0x1b, 0x40);

  const xL = bytesPerRow & 0xff;
  const xH = (bytesPerRow >> 8) & 0xff;

  // Trocear en bandas para respetar el buffer de la impresora.
  for (let startRow = 0; startRow < heightPx; startRow += MAX_BAND_ROWS) {
    const rows = Math.min(MAX_BAND_ROWS, heightPx - startRow);
    const yL = rows & 0xff;
    const yH = (rows >> 8) & 0xff;

    // GS v 0 m xL xH yL yH  (m = 0, modo normal)
    chunks.push(0x1d, 0x76, 0x30, 0x00, xL, xH, yL, yH);

    const from = startRow * bytesPerRow;
    const to = from + rows * bytesPerRow;
    for (let i = from; i < to; i++) chunks.push(data[i]);
  }

  // Avance de papel
  for (let i = 0; i < feedLines; i++) chunks.push(0x0a);

  // Corte: GS V 66 0 (corte con avance)
  if (cut) chunks.push(0x1d, 0x56, 0x42, 0x00);

  return Uint8Array.from(chunks);
}

/**
 * Comando opcional de apertura de cajón monedero (ESC p m t1 t2).
 * Fuera del alcance v1; disponible para uso futuro.
 */
export function openDrawerCommand(): Uint8Array {
  return Uint8Array.from([0x1b, 0x70, 0x00, 0x19, 0xfa]);
}
